import { useState } from 'react';
import { Button, TextButton } from '@safetyhub/ui';
import { ChevronUpIcon, ChevronDownIcon } from '../icons';
import type {
  WorkerStatsEntry,
  AccidentStatsEntry,
} from '../../types/site-stats';
import {
  ACCIDENT_COUNT_FIELDS,
  SMS_WORKER_CATEGORIES,
  createEmptyAccidentEntry,
  createEmptyWorkerEntry,
} from '../../types/site-stats';
import type { PendingProcessType } from '../../hooks/useSiteCreationState';

interface SiteFormStep2Props {
  workerStatsCurrent: WorkerStatsEntry | null;
  workerStatsPrevious: WorkerStatsEntry | null;
  onWorkerStatsCurrentChange: (next: WorkerStatsEntry | null) => void;
  onWorkerStatsPreviousChange: (next: WorkerStatsEntry | null) => void;
  accidentStatsCurrent: AccidentStatsEntry | null;
  accidentStatsPrevious: AccidentStatsEntry | null;
  onAccidentStatsCurrentChange: (next: AccidentStatsEntry | null) => void;
  onAccidentStatsPreviousChange: (next: AccidentStatsEntry | null) => void;
  processTypes: PendingProcessType[];
  onAddProcessType: (name: string) => void;
  onRemoveProcessType: (index: number) => void;
  onMoveProcessType: (index: number, direction: 'up' | 'down') => void;
  products: string;
  onProductsChange: (next: string) => void;
}

/** 문자열 → 비음수 정수. 빈 값/NaN은 0으로 처리 */
function parseNonNegInt(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '') return 0;
  const num = parseInt(trimmed, 10);
  return isNaN(num) ? 0 : Math.max(0, num);
}

interface WorkerStatsSectionProps {
  label: string;
  year: string;
  value: WorkerStatsEntry | null;
  onChange: (next: WorkerStatsEntry | null) => void;
}

function WorkerStatsSection({
  label,
  year,
  value,
  onChange,
}: WorkerStatsSectionProps) {
  const effective = value ?? createEmptyWorkerEntry(year);
  const getCount = (key: string) =>
    effective.categories.find((c) => c.key === key)?.count ?? 0;
  const total = effective.categories.reduce((sum, c) => sum + c.count, 0);

  const setCategory = (catKey: string, raw: string) => {
    const count = parseNonNegInt(raw);
    const nextCategories = SMS_WORKER_CATEGORIES.map((cat) => {
      if (cat.key === catKey) return { key: cat.key, count };
      return {
        key: cat.key,
        count:
          effective.categories.find((c) => c.key === cat.key)?.count ?? 0,
      };
    });
    onChange({ periodValue: year, categories: nextCategories });
  };

  const numberClass =
    'w-full px-2 py-1 border border-ink-300 rounded-control text-sm text-right focus:ring-brand-500 focus:border-brand-500';

  return (
    <div className="rounded-card border border-line p-3">
      <h4 className="text-xs font-medium text-ink-700 mb-2">
        {label} ({year}년)
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {SMS_WORKER_CATEGORIES.map((cat) => (
          <div key={cat.key}>
            <label className="block text-xs text-ink-500 mb-1">
              {cat.label}
            </label>
            <input
              type="number"
              min={0}
              value={getCount(cat.key) === 0 ? '' : String(getCount(cat.key))}
              placeholder="0"
              onChange={(e) => setCategory(cat.key, e.target.value)}
              onFocus={(e) => e.target.select()}
              className={numberClass}
            />
          </div>
        ))}
        <div>
          <label className="block text-xs text-ink-500 mb-1">계(명)</label>
          <span className="block w-full px-2 py-1 text-sm text-right text-ink-700 bg-ink-100 border border-ink-200 rounded-control">
            {total}
          </span>
        </div>
      </div>
    </div>
  );
}

interface AccidentStatsSectionProps {
  label: string;
  year: string;
  value: AccidentStatsEntry | null;
  onChange: (next: AccidentStatsEntry | null) => void;
}

function AccidentStatsSection({
  label,
  year,
  value,
  onChange,
}: AccidentStatsSectionProps) {
  const effective = value ?? createEmptyAccidentEntry(year);
  const total =
    effective.deathCount + effective.injuryCount + effective.diseaseCount;

  const setField = (
    field: 'deathCount' | 'injuryCount' | 'diseaseCount',
    raw: string,
  ) => {
    const count = parseNonNegInt(raw);
    onChange({ ...effective, periodValue: year, [field]: count });
  };

  const numberClass =
    'w-full px-2 py-1 border border-ink-300 rounded-control text-sm text-right focus:ring-brand-500 focus:border-brand-500';

  return (
    <div className="rounded-card border border-line p-3">
      <h4 className="text-xs font-medium text-ink-700 mb-2">
        {label} ({year}년)
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {ACCIDENT_COUNT_FIELDS.map(({ key, label: fieldLabel }) => (
          <div key={key}>
            <label className="block text-xs text-ink-500 mb-1">
              {fieldLabel}
            </label>
            <input
              type="number"
              min={0}
              value={effective[key] === 0 ? '' : String(effective[key])}
              placeholder="0"
              onChange={(e) => setField(key, e.target.value)}
              onFocus={(e) => e.target.select()}
              className={numberClass}
            />
          </div>
        ))}
        <div>
          <label className="block text-xs text-ink-500 mb-1">계</label>
          <span className="block w-full px-2 py-1 text-sm text-right text-ink-700 bg-ink-100 border border-ink-200 rounded-control">
            {total}
          </span>
        </div>
      </div>
    </div>
  );
}

export function SiteFormStep2({
  workerStatsCurrent,
  workerStatsPrevious,
  onWorkerStatsCurrentChange,
  onWorkerStatsPreviousChange,
  accidentStatsCurrent,
  accidentStatsPrevious,
  onAccidentStatsCurrentChange,
  onAccidentStatsPreviousChange,
  processTypes,
  onAddProcessType,
  onRemoveProcessType,
  onMoveProcessType,
  products,
  onProductsChange,
}: SiteFormStep2Props) {
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;
  const currentYearStr = String(currentYear);
  const previousYearStr = String(previousYear);
  const [processInput, setProcessInput] = useState('');

  const handleAddProcessType = () => {
    const trimmed = processInput.trim();
    if (!trimmed) return;
    onAddProcessType(trimmed);
    setProcessInput('');
  };

  const inputClass =
    'w-full px-2 py-1 border border-ink-300 rounded-control text-sm focus:ring-brand-500 focus:border-brand-500';

  return (
    <div className="space-y-6 min-w-0">
      <p className="text-xs text-ink-500">
        안전관리전문기관 조직에서 추가로 수집하는 정보입니다. 건너뛰거나 나중에
        사업장 상세에서 입력할 수 있습니다.
      </p>

      {/* 근로자 현황 */}
      <section className="min-w-0">
        <h3 className="text-sm font-medium text-ink-900 mb-2">
          근로자 현황
        </h3>
        <div className="space-y-2">
          <WorkerStatsSection
            label="당해연도"
            year={currentYearStr}
            value={workerStatsCurrent}
            onChange={onWorkerStatsCurrentChange}
          />
          <WorkerStatsSection
            label="직전년도"
            year={previousYearStr}
            value={workerStatsPrevious}
            onChange={onWorkerStatsPreviousChange}
          />
        </div>
        <p className="mt-1 text-xs text-ink-400">
          입력한 값이 모두 0이면 해당 연도는 저장하지 않습니다.
        </p>
      </section>

      {/* 재해발생 현황 */}
      <section className="min-w-0">
        <h3 className="text-sm font-medium text-ink-900 mb-2">
          재해발생 현황
        </h3>
        <div className="space-y-2">
          <AccidentStatsSection
            label="당해연도"
            year={currentYearStr}
            value={accidentStatsCurrent}
            onChange={onAccidentStatsCurrentChange}
          />
          <AccidentStatsSection
            label="직전년도"
            year={previousYearStr}
            value={accidentStatsPrevious}
            onChange={onAccidentStatsPreviousChange}
          />
        </div>
      </section>

      {/* 공정 */}
      <section className="min-w-0">
        <h3 className="text-sm font-medium text-ink-900 mb-2">공정</h3>
        <p className="text-xs text-ink-500 mb-2">
          주요 공정명을 순서대로 입력합니다. 추가 후 위/아래 버튼으로 순서를
          조정할 수 있습니다.
        </p>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={processInput}
            onChange={(e) => setProcessInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddProcessType();
              }
            }}
            placeholder="예: 철근 조립"
            className={inputClass}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={handleAddProcessType}
            disabled={!processInput.trim()}
            className="flex-shrink-0"
          >
            추가
          </Button>
        </div>
        {processTypes.length === 0 ? (
          <p className="text-xs text-ink-400 text-center py-2">
            등록된 공정이 없습니다.
          </p>
        ) : (
          <ul className="divide-y divide-line-soft border border-line rounded-card">
            {processTypes.map((pt, idx) => (
              <li
                key={`${pt.name}-${idx}`}
                className="flex items-center gap-2 px-3 py-2 min-w-0"
              >
                <span className="flex-1 min-w-0 text-sm text-ink-700 truncate">
                  {pt.name}
                </span>
                <Button
                  variant="ghost"
                  size="xs"
                  iconOnly
                  onClick={() => onMoveProcessType(idx, 'up')}
                  disabled={idx === 0}
                  aria-label={`${pt.name} 위로 이동`}
                  className="flex-shrink-0"
                >
                  <ChevronUpIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  iconOnly
                  onClick={() => onMoveProcessType(idx, 'down')}
                  disabled={idx === processTypes.length - 1}
                  aria-label={`${pt.name} 아래로 이동`}
                  className="flex-shrink-0"
                >
                  <ChevronDownIcon className="w-4 h-4" />
                </Button>
                <TextButton
                  tone="danger"
                  onClick={() => onRemoveProcessType(idx)}
                  className="flex-shrink-0 text-xs"
                >
                  삭제
                </TextButton>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 생산품 */}
      <section className="min-w-0">
        <label
          htmlFor="site-products"
          className="block text-sm font-medium text-ink-900 mb-1"
        >
          생산품
        </label>
        <p className="text-xs text-ink-500 mb-2">
          현장에서 생산하는 주요 품목을 입력하세요. (예: 반도체 웨이퍼, PCB)
        </p>
        <textarea
          id="site-products"
          value={products}
          onChange={(e) => onProductsChange(e.target.value)}
          maxLength={300}
          rows={2}
          placeholder="예: 반도체 웨이퍼, PCB"
          className="block w-full min-w-0 px-2 py-1.5 border border-ink-300 rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
        />
        <div className="mt-1 text-xs text-ink-400 text-right">
          {products.length}/300
        </div>
      </section>
    </div>
  );
}
